import React, { FC, useEffect } from 'react'
import Button from '@mortgage-pos/ui/components/Button'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { rateCodesMap } from '@mortgage-pos/data'
import {
  BankrateRate,
  Product,
  ProductClickedEvent,
  ProductViewedEvent,
} from '@mortgage-pos/types'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import tooltipCopy from '@mortgage-pos/ui/utils/tooltipCopy'
import { formatNumberToDollarAmount } from '@mortgage-pos/utils'
import {
  monthlyPaymentTooltipContent,
  rateOriginTooltipContent,
} from '@mortgage-pos/ui/data/bankrateTooltipData'
import { RATE_SUPERLATIVES } from '@mortgage-pos/ui/data/rateSuperlatives'
import BR_Best_Offer_Circled from '@mortgage-pos/ui/assets/bankrate/BR_Best_Offer_Circled.svg'
import { customTextMap } from '@mortgage-pos/ui/utils/customizationHelpers'

const sageValueProps: string[] = [
  'Leading online experience',
  '35% faster closings than industry average',
  '4.8 (out of 5.0) consumer rating on Bankrate',
]
interface Props {
  rate: BankrateRate
  superlativeName?: string
  handleSubmitRateClick?: () => void
  modifierClass?: string
  lender?: string
  cta?: string
  bestRate?: boolean
}

const RateCardCompare: FC<Props> = ({
  rate,
  superlativeName = 'lowestRate',
  handleSubmitRateClick,
  modifierClass = '',
  lender = '',
  cta = 'Continue',
  bestRate = false,
}) => {
  const { mergedAnswers } = useStoreState((state) => state.answers)

  const { productViewed, productClicked, formContinued } = useStoreActions(
    (state) => state.tracking
  )

  const superlative = RATE_SUPERLATIVES[superlativeName] || 'lowestRate'
  const isCurrentLoan = superlativeName === 'currentLoan'
  const paymentTooltipKey = isCurrentLoan ? 'currentLoan' : 'otherOffers'
  const paymentTooltip = monthlyPaymentTooltipContent[paymentTooltipKey]
  const isSageRateCard = rate?.bankrateAdvertiser?.nmlsLicense === '1374724'

  const { formId } = useStoreState((state) => state.tracking)

  const rateTerm = rateCodesMap[rate?.term]
    ? rateCodesMap[rate.term]
    : rate?.term + ' fixed'

  // remove empty specials
  const advertiserSpecials = rate?.bankrateAdvertiser?.specials.filter(Boolean)

  const tagularProductPayload = !isCurrentLoan
    ? {
        product: {
          brand: rate.bankrateAdvertiser.name,
          category: superlative?.heading || 'lowestRate',
          coupon: rate.discountPoints?.toString(),
          location: '15S2S',
          name: rate.term,
          position: bestRate ? 1 : 2,
          price: rate.apr,
          quantity: Math.round(rate.principleAndInterestPayment),
          upc: rate.rate?.toString(),
          variant: mergedAnswers.loanPurpose,
          productId: bestRate ? '1' : '2',
        } as Product,
        listId: formId,
      }
    : null

  useEffect(() => {
    if (rate) {
      formContinued()
    }

    if (!isCurrentLoan) {
      productViewed(tagularProductPayload as ProductViewedEvent)
    }
  }, [])

  const handleSubmitClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    productClicked(tagularProductPayload as ProductClickedEvent)

    handleSubmitRateClick()
  }

  return (
    <section
      className={`rate-card-compare br-compare ${modifierClass} ${
        bestRate ? 'br-compare__brand-border' : null
      }`}
    >
      {!(superlativeName === 'otherLender' || isCurrentLoan) ? (
        <div className="rate-card-compare__logo-container br-compare__logo-container">
          <div>
            {rate?.bankrateAdvertiser?.logo?.medium ? (
              <img
                src={rate?.bankrateAdvertiser.logo.medium}
                alt={`${rate?.bankrateAdvertiser.name} logo`}
                className="br-compare__lender-logo"
              />
            ) : (
              <h3>{rate?.bankrateAdvertiser?.name}</h3>
            )}
            <div className="br-compare__nmls">
              NMLS #{rate?.bankrateAdvertiser?.nmlsLicense}
            </div>
          </div>
          {bestRate ? (
            <span>
              <img src={BR_Best_Offer_Circled} alt="Best Offer" />
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="rate-card-compare__superlatives-container">
        {superlative.icon ? (
          <img
            src={superlative.icon}
            alt="Recommended offer Icon"
            className="rate-card-compare__superlatives-icon"
          />
        ) : null}
        <span className="rate-card-compare__superlatives-text">
          {superlativeName === 'otherLender' ? lender : superlative.heading}
        </span>
        {isCurrentLoan ? (
          <Tooltip
            className="rate-card-compare__details-tooltip"
            text={rateOriginTooltipContent.text}
            placement="bottom"
          />
        ) : null}
      </div>
      <div className="rate-card-compare__monthly-container">
        <span className="rate-card-compare__monthly-amount">
          {formatDollarAmount(rate?.principleAndInterestPayment)}
        </span>
        <span> /mo.</span>
        <Tooltip
          className="rate-card-compare__details-tooltip"
          placement="bottom"
          text={paymentTooltip.text}
        />
      </div>
      <div className="rate-card-compare__term">{rateTerm}</div>
      <div className="rate-card-compare__details-container">
        <div className="rate-card-compare__rate-container">
          <span>Rate</span>
          <span className="rate-card-compare__details-value">
            {rate?.rate ? `${rate?.rate?.toFixed(3)}%` : 'N/A'}
          </span>
        </div>
        {rate?.apr ? (
          <div className="rate-card-compare__rate-container">
            <span>APR</span>
            <span className="rate-card-compare__details-value">
              {rate.apr?.toFixed(3)}%
            </span>
          </div>
        ) : null}
        {!isCurrentLoan ? (
          <div className="rate-card-compare__points-container">
            <span>Points</span>
            <Tooltip
              title={tooltipCopy.points.title}
              text={tooltipCopy.points.text}
              placement="top"
              className="rate-card-compare__details-tooltip"
            />
            <span className="rate-card-compare__details-value">
              {rate?.discountPoints ? rate?.discountPoints : 'N/A'}&nbsp;
            </span>
          </div>
        ) : null}
        {mergedAnswers.cashOut && !isCurrentLoan ? (
          <div className="rate-card-compare__rate-container">
            <span>Cash-out</span>
            <span className="rate-card-compare__details-value">
              {formatDollarAmount(mergedAnswers.cashOut)}
            </span>
          </div>
        ) : null}
      </div>

      {isSageRateCard ? (
        <div className="br-compare__value-props-container">
          <h2 className="br-compare__value-props-heading">
            Why {customTextMap('sageFullPartnerName')}?
          </h2>
          <ul className="br-compare__value-props-list">
            {sageValueProps.map((valueProp, index) => {
              return (
                <li
                  className="br-compare__value-props-item"
                  key={'valueProp' + index}
                >
                  {valueProp}
                </li>
              )
            })}
          </ul>
        </div>
      ) : advertiserSpecials?.length ? (
        <div className="br-compare__value-props-container">
          <h2 className="br-compare__value-props-heading">
            Why {rate.bankrateAdvertiser?.name}?
          </h2>
          <ul className="br-compare__value-props-list">
            {rate?.bankrateAdvertiser?.specials.map((valueProp, index) => {
              return (
                <li
                  className="br-compare__value-props-item"
                  key={'valueProp' + index}
                >
                  {valueProp}
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}

      {superlativeName === 'otherLender' || isCurrentLoan ? null : (
        <div className="rate-card-compare__lock-button-wrapper">
          <Button
            className="button button--dark rate-card-compare__lock-button br-compare__lock-button"
            content={isSageRateCard ? 'Apply digitally' : cta}
            onClick={handleSubmitClick}
          />
        </div>
      )}
    </section>
  )
}

export default RateCardCompare

function formatDollarAmount(number) {
  return formatNumberToDollarAmount(number, 0)
}
